/**
 * Frontend Analytics Events - Production Grade
 *
 * All events use FLAT format for cross-platform consistency.
 * Flat properties work seamlessly with Web, Backend, and Mobile (Flutter).
 *
 * Event Contract:
 * - Flat properties in code (simple, consistent)
 * - Flat properties in PostHog (easy SQL extraction)
 * - Only send properties with values (nulls/undefined omitted)
 * - User context automatically added from PostHog super properties
 */
import Router from 'next/router';

import { FlatEventProperties } from './flatTypes';
import { getPostHogIdentityProperties } from './identityProperties';
import { posthog } from './posthog';
import { isMemberAnalyticsPath } from './routePolicy';

const LEGACY_GROUP_KEYS = new Set([
  'answer',
  'content',
  'engagement',
  'entity',
  'event',
  'location',
  'navigation',
  'page',
  'parent',
  'poll',
  'profile',
  'search',
  'sponsor',
  'ticket',
  'timing',
]);

function compact(props: FlatEventProperties): FlatEventProperties;
function compact<T extends Record<string, any>>(props: T): Partial<T>;
function compact<T extends Record<string, any>>(props: T): Partial<T> {
  return Object.entries(props).reduce<Partial<T>>((acc, [key, value]) => {
    if (value !== undefined && value !== null && !LEGACY_GROUP_KEYS.has(key)) {
      (acc as Record<string, any>)[key] = value;
    }
    return acc;
  }, {});
}

/**
 * Several early call sites used a grouped analytics shape before the flat
 * PostHog schema landed. Flatten it here so funnels do not silently lose data.
 */
function normalizeProperties(props: FlatEventProperties): FlatEventProperties {
  const source = props as Record<string, any>;
  const normalized = compact(source);

  const entity = source.entity;
  if (entity) {
    normalized.entity_id = normalized.entity_id ?? entity.id;
    normalized.entity_type = normalized.entity_type ?? entity.type;
  }

  const event = source.event;
  if (event) {
    normalized.event_id = normalized.event_id ?? event.id;
    normalized.event_name = normalized.event_name ?? event.name;
    normalized.event_type = normalized.event_type ?? event.type;
    normalized.event_start_date =
      normalized.event_start_date ?? event.startDate;
    normalized.event_role = normalized.event_role ?? event.role;
    normalized.event_session_type =
      normalized.event_session_type ?? event.sessionType;
    normalized.event_subevent_type =
      normalized.event_subevent_type ?? event.subeventType;
  }

  const metadata = source.metadata;
  if (metadata) {
    normalized.event_subevent_type =
      normalized.event_subevent_type ?? metadata.subeventType;
    normalized.event_join_source =
      normalized.event_join_source ?? metadata.joinSource;
    normalized.event_join_target_path =
      normalized.event_join_target_path ?? metadata.targetPath;
    normalized.event_join_cta_text =
      normalized.event_join_cta_text ?? metadata.ctaText;
    normalized.event_join_surface =
      normalized.event_join_surface ?? metadata.surface;
  }

  const parent = source.parent;
  if (parent) {
    normalized.parent_id = normalized.parent_id ?? parent.id;
    normalized.parent_type = normalized.parent_type ?? parent.type;
  }

  const location = source.location;
  if (location) {
    normalized.location_posted_in =
      normalized.location_posted_in ?? location.postedIn;
    normalized.location_group_id =
      normalized.location_group_id ?? location.groupId;
    normalized.location_event_id =
      normalized.location_event_id ?? location.eventId;
    normalized.location_channel_id =
      normalized.location_channel_id ?? location.channelId;
    normalized.location_organization_id =
      normalized.location_organization_id ?? location.organizationId;
  }

  const content = source.content;
  if (content) {
    normalized.content_text = normalized.content_text ?? content.text;
    normalized.content_length = normalized.content_length ?? content.length;
    normalized.content_word_count =
      normalized.content_word_count ?? content.wordCount;
    normalized.content_has_question_mark =
      normalized.content_has_question_mark ?? content.hasQuestionMark;
    normalized.content_has_links =
      normalized.content_has_links ?? content.hasLinks;
    normalized.content_link_count =
      normalized.content_link_count ?? content.linkCount;
    normalized.content_mention_count =
      normalized.content_mention_count ?? content.mentionCount;
    normalized.content_hashtag_count =
      normalized.content_hashtag_count ?? content.hashtagCount;
    normalized.content_hashtags =
      normalized.content_hashtags ?? content.hashtags;
    normalized.content_has_media =
      normalized.content_has_media ?? content.hasMedia;
    normalized.content_media_count =
      normalized.content_media_count ?? content.mediaCount;
    normalized.content_has_tags =
      normalized.content_has_tags ?? content.hasTags;
    normalized.content_has_attachments =
      normalized.content_has_attachments ?? content.hasAttachments;
    normalized.content_attachment_count =
      normalized.content_attachment_count ?? content.attachmentCount;
    normalized.content_type = normalized.content_type ?? content.contentType;
  }
  normalized.content_has_tags =
    normalized.content_has_tags ?? source.post_has_tags;
  normalized.content_has_media =
    normalized.content_has_media ?? source.post_has_media;

  const engagement = source.engagement;
  if (engagement) {
    normalized.engagement_view_duration =
      normalized.engagement_view_duration ?? engagement.viewDuration;
    normalized.engagement_interaction_count =
      normalized.engagement_interaction_count ?? engagement.interactionCount;
    normalized.engagement_completion_rate =
      normalized.engagement_completion_rate ?? engagement.completionRate;
    normalized.engagement_position =
      normalized.engagement_position ?? engagement.position;
    normalized.engagement_reaction_type =
      normalized.engagement_reaction_type ?? engagement.reactionType;
    normalized.reaction_type =
      normalized.reaction_type ?? engagement.reactionType;
    normalized.sponsor_previous_interaction_count =
      normalized.sponsor_previous_interaction_count ??
      engagement.previousInteractionCount;
    normalized.sponsor_days_since_last_interaction =
      normalized.sponsor_days_since_last_interaction ??
      engagement.daysSinceLastInteraction;
  }

  const search = source.search;
  if (search) {
    normalized.search_query = normalized.search_query ?? search.query;
    normalized.search_query_length =
      normalized.search_query_length ?? search.queryLength;
    normalized.search_type = normalized.search_type ?? search.searchType;
    normalized.search_result_count =
      normalized.search_result_count ?? search.resultCount;
    normalized.search_filters = normalized.search_filters ?? search.filters;
    normalized.search_has_filters =
      normalized.search_has_filters ?? search.hasFilters;
    normalized.search_has_results =
      normalized.search_has_results ?? search.hasResults;
  }

  const profile = source.profile;
  if (profile) {
    normalized.profile_viewed_user_id =
      normalized.profile_viewed_user_id ?? profile.viewedUserId;
    normalized.profile_viewed_user_job_title =
      normalized.profile_viewed_user_job_title ?? profile.viewedUserJobTitle;
    normalized.profile_viewed_user_company =
      normalized.profile_viewed_user_company ?? profile.viewedUserCompany;
    normalized.profile_view_source =
      normalized.profile_view_source ?? profile.viewSource;
    normalized.profile_is_first_view =
      normalized.profile_is_first_view ?? profile.isFirstView;
  }

  const sponsor = source.sponsor;
  if (sponsor) {
    normalized.sponsor_id = normalized.sponsor_id ?? sponsor.id;
    normalized.sponsor_name = normalized.sponsor_name ?? sponsor.name;
    normalized.sponsor_tier = normalized.sponsor_tier ?? sponsor.tier;
    normalized.sponsor_content_type =
      normalized.sponsor_content_type ?? sponsor.contentType;
    normalized.sponsor_content_id =
      normalized.sponsor_content_id ?? sponsor.contentId;
    normalized.sponsor_resource_name =
      normalized.sponsor_resource_name ?? sponsor.resourceName;
    normalized.sponsor_resource_type =
      normalized.sponsor_resource_type ?? sponsor.resourceType;
    normalized.sponsor_link_type =
      normalized.sponsor_link_type ?? sponsor.linkType;
    normalized.sponsor_destination_url =
      normalized.sponsor_destination_url ?? sponsor.destinationUrl;
    normalized.sponsor_request_type =
      normalized.sponsor_request_type ?? sponsor.requestType;
    normalized.sponsor_cta_text =
      normalized.sponsor_cta_text ?? sponsor.ctaText;
    normalized.sponsor_has_followed =
      normalized.sponsor_has_followed ?? sponsor.hasFollowed;
  }

  const navigation = source.navigation;
  if (navigation) {
    normalized.navigation_view_source =
      normalized.navigation_view_source ?? navigation.viewSource;
    normalized.metadata_destination =
      normalized.metadata_destination ?? navigation.destination;
    normalized.metadata_label = normalized.metadata_label ?? navigation.label;
    normalized.metadata_section =
      normalized.metadata_section ?? navigation.section;
  }

  const poll = source.poll;
  if (poll) {
    normalized.poll_id = normalized.poll_id ?? poll.pollId;
    normalized.poll_option_id = normalized.poll_option_id ?? poll.optionId;
    normalized.poll_option_text =
      normalized.poll_option_text ?? poll.optionText;
    normalized.poll_option_count =
      normalized.poll_option_count ?? poll.optionCount;
  }

  const answer = source.answer;
  if (answer) {
    normalized.answer_question_id =
      normalized.answer_question_id ?? answer.questionId;
    normalized.answer_vote_direction =
      normalized.answer_vote_direction ?? answer.voteDirection;
    normalized.answer_is_accepted =
      normalized.answer_is_accepted ?? answer.isAccepted;
  }

  const page = source.page;
  if (page) {
    normalized.page_path = normalized.page_path ?? page.path;
    normalized.page_title = normalized.page_title ?? page.title;
    normalized.page_referrer = normalized.page_referrer ?? page.referrer;
    normalized.page_previous_page =
      normalized.page_previous_page ?? page.previousPage;
    normalized.page_time_on_page =
      normalized.page_time_on_page ?? page.timeOnPage;
    normalized.page_scroll_depth =
      normalized.page_scroll_depth ?? page.scrollDepth;
  }

  const timing = source.timing;
  if (timing) {
    normalized.metadata_time_since_creation =
      normalized.metadata_time_since_creation ?? timing.timeSinceCreation;
  }

  return normalized;
}

type FunnelMetadata = {
  funnel_identifier: string;
  parent_funnel_identifier?: string;
  funnel_step_identifier: string;
  funnel_step_order: number;
  funnel_subject_id?: string;
};

const FUNNEL_STEPS: Record<
  string,
  Omit<FunnelMetadata, 'funnel_subject_id'>
> = {
  sponsor_page_viewed: {
    funnel_identifier: 'sponsorship_funnel',
    funnel_step_identifier: 'sponsor_page_viewed',
    funnel_step_order: 10,
  },
  sponsor_content_clicked: {
    funnel_identifier: 'sponsorship_funnel',
    funnel_step_identifier: 'sponsor_content_clicked',
    funnel_step_order: 20,
  },
  sponsor_link_clicked: {
    funnel_identifier: 'sponsorship_funnel',
    funnel_step_identifier: 'sponsor_link_clicked',
    funnel_step_order: 30,
  },
  sponsor_resource_downloaded: {
    funnel_identifier: 'sponsorship_funnel',
    funnel_step_identifier: 'sponsor_resource_downloaded',
    funnel_step_order: 40,
  },
  sponsor_lead_submitted: {
    funnel_identifier: 'sponsorship_funnel',
    funnel_step_identifier: 'sponsor_lead_submitted',
    funnel_step_order: 50,
  },
  organization_followed: {
    funnel_identifier: 'sponsorship_funnel',
    funnel_step_identifier: 'sponsor_followed',
    funnel_step_order: 60,
  },
  organization_unfollowed: {
    funnel_identifier: 'sponsorship_funnel',
    funnel_step_identifier: 'sponsor_unfollowed',
    funnel_step_order: 61,
  },
  conversation_started: {
    funnel_identifier: 'sponsorship_funnel',
    funnel_step_identifier: 'sponsor_message_started',
    funnel_step_order: 70,
  },
  message_sent: {
    funnel_identifier: 'sponsorship_funnel',
    funnel_step_identifier: 'sponsor_message_sent',
    funnel_step_order: 80,
  },
  event_clicked: {
    funnel_identifier: 'event_journey',
    funnel_step_identifier: 'event_clicked',
    funnel_step_order: 10,
  },
  event_registration_clicked: {
    funnel_identifier: 'event_journey',
    funnel_step_identifier: 'registration_clicked',
    funnel_step_order: 20,
  },
  // XPRO user journey
  xpro_landing_viewed: {
    funnel_identifier: 'xpro_journey',
    funnel_step_identifier: 'landing_viewed',
    funnel_step_order: 10,
  },
  xpro_activate_clicked: {
    funnel_identifier: 'xpro_journey',
    funnel_step_identifier: 'activate_clicked',
    funnel_step_order: 20,
  },
  xpro_dashboard_clicked: {
    funnel_identifier: 'xpro_journey',
    funnel_step_identifier: 'dashboard_clicked',
    funnel_step_order: 30,
  },
  xpro_edit_conference_clicked: {
    funnel_identifier: 'xpro_journey',
    funnel_step_identifier: 'edit_conference_clicked',
    funnel_step_order: 40,
  },
  cart_registration_started: {
    funnel_identifier: 'cart_purchase',
    parent_funnel_identifier: 'event_journey',
    funnel_step_identifier: 'registration_started',
    funnel_step_order: 20,
  },
  cart_registration_details_saved: {
    funnel_identifier: 'cart_purchase',
    parent_funnel_identifier: 'event_journey',
    funnel_step_identifier: 'registration_details_saved',
    funnel_step_order: 30,
  },
  cart_registration_completed: {
    funnel_identifier: 'cart_purchase',
    parent_funnel_identifier: 'event_journey',
    funnel_step_identifier: 'registration_completed',
    funnel_step_order: 40,
  },
  cart_order_created: {
    funnel_identifier: 'cart_purchase',
    parent_funnel_identifier: 'event_journey',
    funnel_step_identifier: 'cart_order_created',
    funnel_step_order: 58,
  },
  cart_item_added: {
    funnel_identifier: 'cart_purchase',
    parent_funnel_identifier: 'event_journey',
    funnel_step_identifier: 'cart_item_added',
    funnel_step_order: 10,
  },
  cart_other_item_added: {
    funnel_identifier: 'cart_purchase',
    funnel_step_identifier: 'cart_other_item_added',
    funnel_step_order: 12,
  },
  cart_item_quantity_changed: {
    funnel_identifier: 'cart_purchase',
    parent_funnel_identifier: 'event_journey',
    funnel_step_identifier: 'cart_quantity_changed',
    funnel_step_order: 14,
  },
  cart_item_removed: {
    funnel_identifier: 'cart_purchase',
    parent_funnel_identifier: 'event_journey',
    funnel_step_identifier: 'cart_item_removed',
    funnel_step_order: 16,
  },
  checkout_initiated: {
    funnel_identifier: 'cart_purchase',
    parent_funnel_identifier: 'event_journey',
    funnel_step_identifier: 'checkout_initiated',
    funnel_step_order: 50,
  },
  promo_code_applied: {
    funnel_identifier: 'cart_purchase',
    parent_funnel_identifier: 'event_journey',
    funnel_step_identifier: 'promo_code_applied',
    funnel_step_order: 45,
  },
  discount_applied: {
    funnel_identifier: 'cart_purchase',
    parent_funnel_identifier: 'event_journey',
    funnel_step_identifier: 'discount_applied',
    funnel_step_order: 46,
  },
  promo_code_failed: {
    funnel_identifier: 'cart_purchase',
    parent_funnel_identifier: 'event_journey',
    funnel_step_identifier: 'promo_code_failed',
    funnel_step_order: 47,
  },
  checkout_payment_succeeded: {
    funnel_identifier: 'cart_purchase',
    parent_funnel_identifier: 'event_journey',
    funnel_step_identifier: 'checkout_payment_succeeded',
    funnel_step_order: 60,
  },
  checkout_payment_failed: {
    funnel_identifier: 'cart_purchase',
    parent_funnel_identifier: 'event_journey',
    funnel_step_identifier: 'checkout_payment_failed',
    funnel_step_order: 61,
  },
  checkout_completed: {
    funnel_identifier: 'cart_purchase',
    parent_funnel_identifier: 'event_journey',
    funnel_step_identifier: 'checkout_completed',
    funnel_step_order: 65,
  },
  cart_item_purchased: {
    funnel_identifier: 'cart_purchase',
    parent_funnel_identifier: 'event_journey',
    funnel_step_identifier: 'cart_item_purchased',
    funnel_step_order: 70,
  },
  event_registered: {
    funnel_identifier: 'event_journey',
    funnel_step_identifier: 'event_registered',
    funnel_step_order: 60,
  },
  conference_subevent_clicked: {
    funnel_identifier: 'event_journey',
    funnel_step_identifier: 'join_clicked',
    funnel_step_order: 70,
  },
  event_join_clicked: {
    funnel_identifier: 'event_journey',
    funnel_step_identifier: 'join_clicked',
    funnel_step_order: 70,
  },
  event_join_on_tradewing_clicked: {
    funnel_identifier: 'event_journey',
    funnel_step_identifier: 'join_on_tradewing_clicked',
    funnel_step_order: 72,
  },
  event_waiting_room_join_clicked: {
    funnel_identifier: 'event_journey',
    funnel_step_identifier: 'waiting_room_join_clicked',
    funnel_step_order: 73,
  },
  event_join_landing_viewed: {
    funnel_identifier: 'event_journey',
    funnel_step_identifier: 'join_landing_viewed',
    funnel_step_order: 75,
  },
  event_meet_join_clicked: {
    funnel_identifier: 'event_journey',
    funnel_step_identifier: 'meet_join_clicked',
    funnel_step_order: 80,
  },
  conference_session_joined: {
    funnel_identifier: 'event_journey',
    funnel_step_identifier: 'event_joined',
    funnel_step_order: 85,
  },
  event_joined: {
    funnel_identifier: 'event_journey',
    funnel_step_identifier: 'event_joined',
    funnel_step_order: 85,
  },
  agora_attendance_tracked: {
    funnel_identifier: 'event_journey',
    funnel_step_identifier: 'agora_attendance_tracked',
    funnel_step_order: 90,
  },
  event_recording_viewed: {
    funnel_identifier: 'event_journey',
    funnel_step_identifier: 'recording_viewed',
    funnel_step_order: 100,
  },
  membership_plan_viewed: {
    funnel_identifier: 'membership_lifecycle',
    funnel_step_identifier: 'membership_plan_viewed',
    funnel_step_order: 10,
  },
  membership_plan_selected: {
    funnel_identifier: 'membership_lifecycle',
    funnel_step_identifier: 'membership_plan_selected',
    funnel_step_order: 20,
  },
  membership_checkout_started: {
    funnel_identifier: 'membership_lifecycle',
    funnel_step_identifier: 'membership_checkout_started',
    funnel_step_order: 30,
  },
  membership_info_saved: {
    funnel_identifier: 'membership_lifecycle',
    funnel_step_identifier: 'membership_info_saved',
    funnel_step_order: 40,
  },
  membership_payment_plan_selected: {
    funnel_identifier: 'membership_lifecycle',
    funnel_step_identifier: 'membership_payment_plan_selected',
    funnel_step_order: 45,
  },
  membership_autopay_enabled: {
    funnel_identifier: 'membership_lifecycle',
    funnel_step_identifier: 'membership_autopay_enabled',
    funnel_step_order: 50,
  },
  membership_autopay_disabled: {
    funnel_identifier: 'membership_churn',
    parent_funnel_identifier: 'membership_lifecycle',
    funnel_step_identifier: 'membership_autopay_disabled',
    funnel_step_order: 10,
  },
  membership_no_activity_30_days: {
    funnel_identifier: 'membership_churn',
    parent_funnel_identifier: 'membership_lifecycle',
    funnel_step_identifier: 'membership_no_activity_30_days',
    funnel_step_order: 5,
  },
  membership_group_left: {
    funnel_identifier: 'membership_churn',
    parent_funnel_identifier: 'membership_lifecycle',
    funnel_step_identifier: 'membership_group_left',
    funnel_step_order: 15,
  },
  membership_login_issue: {
    funnel_identifier: 'membership_churn',
    parent_funnel_identifier: 'membership_lifecycle',
    funnel_step_identifier: 'membership_login_issue',
    funnel_step_order: 25,
  },
  membership_payment_initiated: {
    funnel_identifier: 'membership_lifecycle',
    funnel_step_identifier: 'membership_payment_initiated',
    funnel_step_order: 60,
  },
  membership_payment_succeeded: {
    funnel_identifier: 'membership_lifecycle',
    funnel_step_identifier: 'membership_payment_succeeded',
    funnel_step_order: 70,
  },
  membership_payment_failed: {
    funnel_identifier: 'membership_churn',
    parent_funnel_identifier: 'membership_lifecycle',
    funnel_step_identifier: 'membership_payment_failed',
    funnel_step_order: 20,
  },
  membership_invoice_created: {
    funnel_identifier: 'membership_lifecycle',
    funnel_step_identifier: 'membership_invoice_created',
    funnel_step_order: 65,
  },
  membership_queued: {
    funnel_identifier: 'membership_lifecycle',
    funnel_step_identifier: 'membership_queued',
    funnel_step_order: 70,
  },
  membership_expired: {
    funnel_identifier: 'membership_churn',
    parent_funnel_identifier: 'membership_lifecycle',
    funnel_step_identifier: 'membership_expired',
    funnel_step_order: 30,
  },
  membership_cancelled: {
    funnel_identifier: 'membership_churn',
    parent_funnel_identifier: 'membership_lifecycle',
    funnel_step_identifier: 'membership_cancelled',
    funnel_step_order: 40,
  },
  user_deactivated: {
    funnel_identifier: 'membership_churn',
    parent_funnel_identifier: 'membership_lifecycle',
    funnel_step_identifier: 'user_deactivated',
    funnel_step_order: 50,
  },
};

const POST_EVENT_ENGAGEMENT_EVENTS = new Set([
  'post_created',
  'post_liked',
  'post_unliked',
  'comment_created',
  'comment_liked',
  'comment_unliked',
  'poll_voted',
  'answer_created',
  'answer_voted',
  'announcement_read',
  'post_read',
]);

function resolveFunnelSubjectId(
  props: FlatEventProperties
): string | undefined {
  if (props.cart_order_id) return String(props.cart_order_id);
  if (props.entity_type === 'cart_order' && props.entity_id) {
    return String(props.entity_id);
  }
  if (props.payment_item_type === 'cart_order' && props.payment_item_id) {
    return String(props.payment_item_id);
  }
  if (props.sponsor_id) return String(props.sponsor_id);
  if (props.event_sponsor_id) return String(props.event_sponsor_id);
  if (props.event_id) return String(props.event_id);
  if (props.membership_id) return String(props.membership_id);
  if (props.membership_type_id) return String(props.membership_type_id);
  if (props.payment_item_type === 'membership' && props.payment_item_id) {
    return String(props.payment_item_id);
  }
  if (props.location_event_id) return String(props.location_event_id);
  if (props.location_organization_id && props.is_sponsor_interaction) {
    return String(props.location_organization_id);
  }
  if (props.entity_type === 'sponsor' && props.entity_id)
    return String(props.entity_id);
  if (props.entity_type === 'sponsor_content' && props.entity_id)
    return String(props.entity_id);
  if (props.entity_type === 'sponsor_link' && props.entity_id)
    return String(props.entity_id);
  if (props.entity_type === 'organization_lead' && props.sponsor_id)
    return String(props.sponsor_id);
  if (props.entity_type === 'event' && props.entity_id)
    return String(props.entity_id);
  if (
    [
      'membership',
      'membership_order',
      'membership_term',
      'membership_plan',
    ].includes(String(props.entity_type)) &&
    props.entity_id
  ) {
    return String(props.entity_id);
  }
  if (props.parent_type === 'event' && props.parent_id)
    return String(props.parent_id);
  return undefined;
}

function getFunnelMetadata(
  eventName: string,
  props: FlatEventProperties
): Partial<FunnelMetadata> {
  if (
    eventName === 'event_join_clicked' &&
    (props.entity_type === 'quick_call' || props.entity_type === 'quick_meet')
  ) {
    return compact<Partial<FunnelMetadata>>({
      funnel_identifier:
        props.entity_type === 'quick_call'
          ? 'quick_call_journey'
          : 'quick_meet_journey',
      funnel_step_identifier: 'join_clicked',
      funnel_step_order: 5,
      funnel_subject_id: props.entity_id,
    }) as Partial<FunnelMetadata>;
  }
  const configuredStep = FUNNEL_STEPS[eventName];
  const funnelSubjectId = resolveFunnelSubjectId(props);

  const isSponsorFunnelEvent =
    configuredStep?.funnel_identifier === 'sponsorship_funnel';

  if (
    configuredStep &&
    isSponsorFunnelEvent &&
    !(
      props.is_sponsor_interaction ||
      props.sponsor_id ||
      props.event_sponsor_id ||
      props.entity_type === 'sponsor' ||
      props.entity_type === 'sponsor_content' ||
      props.entity_type === 'sponsor_link' ||
      props.entity_type === 'organization_lead'
    )
  ) {
    return {};
  }

  if (configuredStep) {
    return compact<Partial<FunnelMetadata>>({
      ...configuredStep,
      parent_funnel_identifier: funnelSubjectId
        ? configuredStep.parent_funnel_identifier
        : undefined,
      funnel_subject_id: funnelSubjectId,
    }) as Partial<FunnelMetadata>;
  }

  const isPostEventEngagement =
    POST_EVENT_ENGAGEMENT_EVENTS.has(eventName) &&
    (Boolean(funnelSubjectId) || props.post_is_announcement === true);

  if (!isPostEventEngagement) return {};

  return compact<Partial<FunnelMetadata>>({
    funnel_identifier: 'event_journey',
    funnel_step_identifier: `announcement_${eventName}`,
    funnel_step_order: 110,
    funnel_subject_id: funnelSubjectId,
  }) as Partial<FunnelMetadata>;
}

function getSponsorRouteMetadata(
  eventName: string,
  props: FlatEventProperties
): FlatEventProperties {
  if (typeof window === 'undefined') return {};

  const pathname = window.location.pathname;
  const sponsorMatch = pathname.match(/^\/sponsors\/([^/]+)/);
  const isSponsorsDirectory = pathname === '/sponsors';
  const isSponsorRoute = isSponsorsDirectory || Boolean(sponsorMatch);

  if (!isSponsorRoute) return {};

  const sponsorId =
    props.sponsor_id ?? props.location_organization_id ?? sponsorMatch?.[1];
  const sponsorInteractionEvents = new Set([
    'post_created',
    'post_liked',
    'post_unliked',
    'comment_created',
    'comment_liked',
    'comment_unliked',
    'poll_voted',
    'answer_created',
    'answer_voted',
    'conversation_started',
    'message_sent',
    'organization_followed',
    'organization_unfollowed',
    'sponsor_page_viewed',
    'sponsor_content_clicked',
    'sponsor_link_clicked',
    'sponsor_resource_downloaded',
    'sponsor_lead_submitted',
  ]);

  if (!sponsorInteractionEvents.has(eventName) && !isSponsorsDirectory)
    return {};

  return compact<FlatEventProperties>({
    is_sponsor_interaction: true,
    sponsor_interaction_source: isSponsorsDirectory
      ? 'sponsors_directory'
      : 'sponsor_detail',
    sponsor_id: sponsorId,
    location_organization_id: props.location_organization_id ?? sponsorId,
  });
}

/**
 * Core track function - accepts ONLY flat format
 * Sends flat properties directly to PostHog
 * Automatically groups user context from super properties
 * Omits null/undefined properties automatically
 */
function track(eventName: string, props: FlatEventProperties): void {
  if (typeof window === 'undefined') return;
  // XPRO user journey: admin funnel clicks live on /admin; other FE events stay member-only
  const isXproAdminJourneyEvent =
    FUNNEL_STEPS[eventName]?.funnel_identifier === 'xpro_journey';
  if (!isMemberAnalyticsPath(window.location.pathname) && !isXproAdminJourneyEvent) {
    return;
  }
  try {
    const flatProps = normalizeProperties({
      ...props,
      ...getSponsorRouteMetadata(eventName, normalizeProperties(props)),
    });
    const funnelProps = getFunnelMetadata(eventName, flatProps);
    const sessionId = posthog.get_session_id();
    const identityProps = getPostHogIdentityProperties();

    const payload = {
      event: eventName,
      event_version: 1,
      event_type: 'custom',
      // Identity and tenant values remain ordinary event properties for the
      // warehouse. Avoid updating a paid PostHog person profile for every
      // interaction; identifyUser owns lifecycle/profile updates.
      $process_person_profile: false,
      interaction_type: flatProps.interaction_type ?? 'active',
      session_id: sessionId,
      timestamp: new Date().toISOString(),
      source: 'frontend' as const,
      route: Router.pathname,
      ...flatProps,
      ...funnelProps,
      ...identityProps,
    };

    posthog.capture(eventName, payload);
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[Analytics] Failed to capture ${eventName}`, error);
    }
  }
}

// ===========================================================================
// COMMUNITY — Posts, Comments, Polls, Q&A
// ===========================================================================

/** User created a post (message, article, poll, question) */
export function post_created(props: FlatEventProperties): void {
  track('post_created', props);
}

/** User liked / reacted to a post */
export function post_liked(props: FlatEventProperties): void {
  track('post_liked', props);
}

/** User removed their like / reaction from a post */
export function post_unliked(props: FlatEventProperties): void {
  track('post_unliked', props);
}

/** User created a comment on a post */
export function comment_created(props: FlatEventProperties): void {
  track('comment_created', props);
}

/** User liked a comment */
export function comment_liked(props: FlatEventProperties): void {
  track('comment_liked', props);
}

/** User removed their like from a comment */
export function comment_unliked(props: FlatEventProperties): void {
  track('comment_unliked', props);
}

/** User voted on a poll option */
export function poll_voted(props: FlatEventProperties): void {
  track('poll_voted', props);
}

/** User created an answer to a question */
export function answer_created(props: FlatEventProperties): void {
  track('answer_created', props);
}

/** User voted on an answer (upvote/downvote) */
export function answer_voted(props: FlatEventProperties): void {
  track('answer_voted', props);
}

// ===========================================================================
// GROUPS
// ===========================================================================

/** User joined a group */
export function group_joined(props: FlatEventProperties): void {
  track('group_joined', props);
}

/** User left a group */
export function group_left(props: FlatEventProperties): void {
  track('group_left', props);
}

/** User viewed a group page */
export function group_page_viewed(props: FlatEventProperties): void {
  track('group_page_viewed', props);
}

/** User sent a group invitation */
export function group_invitation_sent(props: FlatEventProperties): void {
  track('group_invitation_sent', props);
}

/** User accepted a group invitation */
export function group_invitation_accepted(props: FlatEventProperties): void {
  track('group_invitation_accepted', props);
}

// ===========================================================================
// ORGANIZATIONS / SPONSORS
// ===========================================================================

/** User followed an organization/sponsor */
export function organization_followed(props: FlatEventProperties): void {
  track('organization_followed', props);
}

/** User unfollowed an organization/sponsor */
export function organization_unfollowed(props: FlatEventProperties): void {
  track('organization_unfollowed', props);
}

/** User submitted a lead/demo request form */
export function sponsor_lead_submitted(props: FlatEventProperties): void {
  track('sponsor_lead_submitted', props);
}

/** User viewed a sponsor/organization page */
export function sponsor_page_viewed(props: FlatEventProperties): void {
  track('sponsor_page_viewed', props);
}

/** User clicked on sponsor content */
export function sponsor_content_clicked(props: FlatEventProperties): void {
  track('sponsor_content_clicked', props);
}

/** User downloaded a sponsor resource */
export function sponsor_resource_downloaded(props: FlatEventProperties): void {
  track('sponsor_resource_downloaded', props);
}

/** User clicked an external sponsor link */
export function sponsor_link_clicked(props: FlatEventProperties): void {
  track('sponsor_link_clicked', props);
}

// ===========================================================================
// EVENTS (Conferences, Simple Events)
// ===========================================================================

/** User registered for an event */
export function event_registered(props: FlatEventProperties): void {
  track('event_registered', props);
}

/** User clicked register button for an event */
export function event_registration_clicked(props: FlatEventProperties): void {
  track('event_registration_clicked', props);
}

/** User started the ticket attendee registration flow from the cart sub-funnel */
export function cart_registration_started(props: FlatEventProperties): void {
  track('cart_registration_started', props);
}

/** User saved attendee registration details */
export function cart_registration_details_saved(
  props: FlatEventProperties
): void {
  track('cart_registration_details_saved', props);
}

/** User completed all attendee registration details before checkout */
export function cart_registration_completed(props: FlatEventProperties): void {
  track('cart_registration_completed', props);
}

/** User joined a conference session or booth */
export function conference_session_joined(props: FlatEventProperties): void {
  track('conference_session_joined', props);
}

/** User clicked to join a conference sub-event */
export function conference_subevent_clicked(props: FlatEventProperties): void {
  track('conference_subevent_clicked', props);
}

/** User clicked a final Join on Tradewing CTA for an event/session/booth */
export function event_join_clicked(props: FlatEventProperties): void {
  track('event_join_clicked', props);
}

/** User clicked a Join on Tradewing CTA before the Agora meet route */
export function event_join_on_tradewing_clicked(
  props: FlatEventProperties
): void {
  track('event_join_on_tradewing_clicked', props);
}

/** Speaker/admin clicked into a waiting room */
export function event_waiting_room_join_clicked(
  props: FlatEventProperties
): void {
  track('event_waiting_room_join_clicked', props);
}

/** User landed on the event/session join page before entering the call */
export function event_join_landing_viewed(props: FlatEventProperties): void {
  track('event_join_landing_viewed', props);
}

/** User clicked the final Agora meet Join Now action */
export function event_meet_join_clicked(props: FlatEventProperties): void {
  track('event_meet_join_clicked', props);
}

/** User clicked to view an event */
export function event_clicked(props: FlatEventProperties): void {
  track('event_clicked', props);
}

// XPRO user journey: admin funnel clicks
export function xpro_landing_viewed(props: FlatEventProperties = {}): void {
  track('xpro_landing_viewed', props);
}

export function xpro_activate_clicked(props: FlatEventProperties = {}): void {
  track('xpro_activate_clicked', props);
}

export function xpro_dashboard_clicked(props: FlatEventProperties): void {
  track('xpro_dashboard_clicked', props);
}

export function xpro_edit_conference_clicked(props: FlatEventProperties): void {
  track('xpro_edit_conference_clicked', props);
}

/** User joined a simple/virtual event */
export function event_joined(props: FlatEventProperties): void {
  track('event_joined', props);
}

/** User sent a chat message in a conference */
export function conference_chat_sent(props: FlatEventProperties): void {
  track('conference_chat_sent', props);
}

/** User cancelled an event registration */
export function event_cancelled(props: FlatEventProperties): void {
  track('event_cancelled', props);
}

/** User viewed an event recording */
export function event_recording_viewed(props: FlatEventProperties): void {
  track('event_recording_viewed', props);
}

/** Backend or frontend attendance signal from Agora */
export function agora_attendance_tracked(props: FlatEventProperties): void {
  track('agora_attendance_tracked', props);
}

// ===========================================================================
// CART & CHECKOUT
// ===========================================================================

/** Item added to cart */
export function cart_item_added(props: FlatEventProperties): void {
  track('cart_item_added', props);
}

/** Non-ticket item added to cart */
export function cart_other_item_added(props: FlatEventProperties): void {
  track('cart_other_item_added', props);
}

/** User changed a cart item's quantity in either direction */
export function cart_item_quantity_changed(props: FlatEventProperties): void {
  track('cart_item_quantity_changed', props);
}

/** Item removed from cart */
export function cart_item_removed(props: FlatEventProperties): void {
  track('cart_item_removed', props);
}

/** User initiated checkout */
export function checkout_initiated(props: FlatEventProperties): void {
  track('checkout_initiated', props);
}

/** Cart order created before payment confirmation */
export function cart_order_created(props: FlatEventProperties): void {
  track('cart_order_created', props);
}

/** Promo code applied successfully */
export function promo_code_applied(props: FlatEventProperties): void {
  track('promo_code_applied', props);
}

/** Promo code failed */
export function promo_code_failed(props: FlatEventProperties): void {
  track('promo_code_failed', props);
}

/** Discount appeared in cart totals */
export function discount_applied(props: FlatEventProperties): void {
  track('discount_applied', props);
}

/** Cart checkout payment succeeded */
export function checkout_payment_succeeded(props: FlatEventProperties): void {
  track('checkout_payment_succeeded', props);
}

/** Cart checkout payment failed */
export function checkout_payment_failed(props: FlatEventProperties): void {
  track('checkout_payment_failed', props);
}

/** Payment completed successfully */
export function checkout_completed(props: FlatEventProperties): void {
  track('checkout_completed', props);
}

/** Purchased ticket or cart item after cart order completion */
export function cart_item_purchased(props: FlatEventProperties): void {
  track('cart_item_purchased', props);
}

export function ticket_purchased(props: FlatEventProperties): void {
  track('ticket_purchased', props);
}

// ===========================================================================
// MEMBERSHIP
// ===========================================================================

/** User selected a membership plan */
export function membership_plan_selected(props: FlatEventProperties): void {
  track('membership_plan_selected', props);
}

/** User viewed a membership plan or membership listing */
export function membership_plan_viewed(props: FlatEventProperties): void {
  track('membership_plan_viewed', props);
}

/** User entered membership checkout */
export function membership_checkout_started(props: FlatEventProperties): void {
  track('membership_checkout_started', props);
}

/** User saved membership checkout info */
export function membership_info_saved(props: FlatEventProperties): void {
  track('membership_info_saved', props);
}

/** User selected annual/monthly/no-payment membership terms */
export function membership_payment_plan_selected(
  props: FlatEventProperties
): void {
  track('membership_payment_plan_selected', props);
}

/** User enabled automatic renewal */
export function membership_autopay_enabled(props: FlatEventProperties): void {
  track('membership_autopay_enabled', props);
}

/** User disabled automatic renewal */
export function membership_autopay_disabled(props: FlatEventProperties): void {
  track('membership_autopay_disabled', props);
}

/** Membership payment was submitted */
export function membership_payment_initiated(props: FlatEventProperties): void {
  track('membership_payment_initiated', props);
}

/** Membership payment succeeded */
export function membership_payment_succeeded(props: FlatEventProperties): void {
  track('membership_payment_succeeded', props);
}

/** Membership payment failed */
export function membership_payment_failed(props: FlatEventProperties): void {
  track('membership_payment_failed', props);
}

/** Membership invoice was generated */
export function membership_invoice_created(props: FlatEventProperties): void {
  track('membership_invoice_created', props);
}

/** Future-dated membership was queued */
export function membership_queued(props: FlatEventProperties): void {
  track('membership_queued', props);
}

/** Membership expired/lapsed */
export function membership_expired(props: FlatEventProperties): void {
  track('membership_expired', props);
}

/** Membership was cancelled */
export function membership_cancelled(props: FlatEventProperties): void {
  track('membership_cancelled', props);
}

/** Member had no tracked activity for 30 days */
export function membership_no_activity_30_days(
  props: FlatEventProperties
): void {
  track('membership_no_activity_30_days', props);
}

/** Member left a group, which can contribute to membership churn risk */
export function membership_group_left(props: FlatEventProperties): void {
  track('membership_group_left', props);
}

/** Member had a login issue, which can contribute to membership churn risk */
export function membership_login_issue(props: FlatEventProperties): void {
  track('membership_login_issue', props);
}

/** User was deactivated */
export function user_deactivated(props: FlatEventProperties): void {
  track('user_deactivated', props);
}

// ===========================================================================
// MESSAGES (Stream Chat)
// ===========================================================================

/** User started a new conversation */
export function conversation_started(props: FlatEventProperties): void {
  track('conversation_started', props);
}

/** User sent a message */
export function message_sent(props: FlatEventProperties): void {
  track('message_sent', props);
}

/** User read a message */
export function message_read(props: FlatEventProperties): void {
  track('message_read', props);
}

// ===========================================================================
// RESOURCES / LIBRARY
// ===========================================================================

/** User downloaded a resource */
export function resource_downloaded(props: FlatEventProperties): void {
  track('resource_downloaded', props);
}

/** User uploaded a resource */
export function resource_uploaded(props: FlatEventProperties): void {
  track('resource_uploaded', props);
}

/** User viewed a resource */
export function resource_viewed(props: FlatEventProperties): void {
  track('resource_viewed', props);
}

/** User searched the resource library */
export function resource_searched(props: FlatEventProperties): void {
  track('resource_searched', props);
}

// ===========================================================================
// SEARCH & DISCOVERY
// ===========================================================================

/** User clicked a search result */
export function search_result_clicked(props: FlatEventProperties): void {
  track('search_result_clicked', props);
}

/** User submitted a search query */
export function search_query_submitted(props: FlatEventProperties): void {
  track('search_query_submitted', props);
}

/** User viewed another member's profile */
export function profile_viewed(props: FlatEventProperties): void {
  track('profile_viewed', props);
}

/** User searched the member directory */
export function directory_searched(props: FlatEventProperties): void {
  track('directory_searched', props);
}

/** User clicked a contact affordance on a member profile */
export function profile_contact_clicked(props: FlatEventProperties): void {
  track('profile_contact_clicked', props);
}

/** User clicked a support/contact-support link */
export function support_contact_clicked(props: FlatEventProperties): void {
  track('support_contact_clicked', props);
}

// ===========================================================================
// NAVIGATION
// ===========================================================================

/** User clicked a navigation item */
export function navigation_clicked(props: FlatEventProperties): void {
  track('navigation_clicked', props);
}

// ===========================================================================
// SESSION / AUTH
// ===========================================================================

/** User viewed login UI */
export function login_ui_viewed(props: FlatEventProperties): void {
  track('login_ui_viewed', props);
}

/** User clicked continue with SSO */
export function login_sso_clicked(props: FlatEventProperties): void {
  track('login_sso_clicked', props);
}

/** User clicked reset password */
export function login_reset_password_clicked(props: FlatEventProperties): void {
  track('login_reset_password_clicked', props);
}

/** User clicked sign up */
export function login_signup_clicked(props: FlatEventProperties): void {
  track('login_signup_clicked', props);
}

/** User clicked login */
export function login_clicked(props: FlatEventProperties): void {
  track('login_clicked', props);
}

/** Login outcome (success or failure) */
export function login_outcome(props: FlatEventProperties): void {
  track('login_outcome', props);
}

/** Session started (after login confirmed) */
export function session_started(props: FlatEventProperties): void {
  track('session_started', props);
}

/** Session ended (on logout) */
export function session_ended(props: FlatEventProperties): void {
  track('session_ended', props);
}

// ===========================================================================
// FEED ENGAGEMENT
// ===========================================================================

/** Entity was visible in feed for ≥5 seconds */
export function entity_read(props: FlatEventProperties): void {
  const flatProps = normalizeProperties(props);
  track(`${flatProps.entity_type}_read`, flatProps);
}

// ===========================================================================
// PROFILE & ACCOUNT
// ===========================================================================

/** User updated their profile */
export function profile_updated(props: FlatEventProperties): void {
  track('profile_updated', props);
}

/** User viewed a speaker profile */
export function speaker_profile_viewed(props: FlatEventProperties): void {
  track('speaker_profile_viewed', props);
}

// ===========================================================================
// CONTENT LIFECYCLE
// ===========================================================================

/** User edited their own content */
export function content_edited(props: FlatEventProperties): void {
  track('content_edited', props);
}

/** User deleted their own content */
export function content_deleted(props: FlatEventProperties): void {
  track('content_deleted', props);
}

/** User shared content */
export function content_shared(props: FlatEventProperties): void {
  track('content_shared', props);
}

/** User bookmarked/saved content */
export function content_bookmarked(props: FlatEventProperties): void {
  track('content_bookmarked', props);
}

// ===========================================================================
// NETWORK INTELLIGENCE
// ===========================================================================

/** User interacted with another member */
export function member_interaction(props: FlatEventProperties): void {
  track('member_interaction', props);
}

/** User sent a connection request */
export function connection_request_sent(props: FlatEventProperties): void {
  track('connection_request_sent', props);
}

/** User accepted a connection request */
export function connection_request_accepted(props: FlatEventProperties): void {
  track('connection_request_accepted', props);
}

// ===========================================================================
// FEATURE ADOPTION
// ===========================================================================

/** User used a feature for the first time */
export function feature_first_used(props: FlatEventProperties): void {
  track('feature_first_used', props);
}
